/**
 * THE CHANNEL MODEL — the axis that replaces the four Insights tabs.
 *
 * Contract: `docs/planning/mora-growth-intelligence-overhaul-2026-08-26/48-ONE-ANALYTICS-PAGE-SPEC.md` §3.
 *
 * ── THE RULE, AND IT IS NOT A FILTER ────────────────────────────────────────
 * 🔴 **Channel REPLACES the card set. It does not filter it.** `?channel=`
 * selects which registry rows render. Two channels may share a card `id`
 * (`landing-pages` appears under both paid channels) and a card's body may read
 * a different source per channel — but the reader is never shown one card set
 * with rows greyed out. Measured on the reference surface 2026-08-27: switching
 * `?channel=meta` does not dim the Shopify cards, it renders a different seven.
 *
 * ── WHY THIS AXIS AND NOT `?view=` ──────────────────────────────────────────
 * `view` had four values — Overview / Audience / Content / Competitors — and
 * every one of them was a CATEGORY OF CARD rather than a source of truth. That
 * is why `WhosBuyingSection` ("Where your orders come from", a Shopify read)
 * lived on the Audience tab, and why `MetaTrafficInAnalyticsCard` sat on
 * Overview with nothing on the page explaining what it was doing there. A
 * reader looking for their Shopify numbers had to know which of four card
 * categories someone had filed them under.
 *
 * A channel is a question the reader actually has ("what is Meta doing?"), and
 * every card under it reads a source the reader connected. The four tabs are
 * mapped, card by card, in the registry — nothing is dropped, see
 * {@link LEGACY_VIEW_TO_CHANNEL} and the spec's §4.
 *
 * ── `organic` IS FIRST-CLASS, AND IT IS OURS ────────────────────────────────
 * The reference surface has no organic object at all. Mora's `organic` channel
 * is not a leftover bucket for "everything that is not paid": it is the channel
 * that owns every social read, it carries the wedge card (a post beside the
 * sessions and orders that followed it), and it is registered with exactly the
 * same shape as the paid channels.
 *
 * 🔴 What it does NOT get is a fabricated efficiency number. ROAS, MER, CPA,
 * CAC and cost-per-anything are registered only for channels that have a spend
 * source. `organic` has no spend, so those cards are **not registered for it** —
 * not printed as `0`, not printed as `—`. A ratio whose denominator does not
 * exist is not an empty measurement, it is a category error, and an em-dash
 * would invite the reader to believe the number is merely late. See
 * {@link channelHasSpend}.
 */

import type { AnalyticsViewId } from '@/lib/analytics/analytics-filter';

/** The seven channels, in picker order. */
export const INSIGHTS_CHANNEL_IDS = [
  'all',
  'shopify',
  'google-ads',
  'meta-ads',
  'linkedin-ads',
  'ga4',
  'organic',
] as const;

export type InsightsChannelId = (typeof INSIGHTS_CHANNEL_IDS)[number];

export const INSIGHTS_CHANNEL_DEFAULT: InsightsChannelId = 'all';

export interface InsightsChannelDef {
  id: InsightsChannelId;
  /** The rendered label. One string, one place — the picker, the context line
   *  and the "Manage metrics" subtitle all read this, so they cannot drift. */
  label: string;
  /**
   * Which integration must be connected for this channel to be AVAILABLE.
   * `null` = always available (`all` reads whatever exists).
   *
   * 🔴 This gates the CHANNEL, never a card inside it. Per spec §2.3 rule 2: a
   * card is absent only when its whole channel is unavailable. Choosing Meta is
   * a request for Meta, and seven honest empties are the answer — a blank page
   * is not.
   */
  requiresSource: InsightsSourceId | null;
  /**
   * Does this channel have a spend source? Gates every cost-based metric.
   * The single most important field on this type — see the class-error note in
   * the module docblock.
   */
  hasSpend: boolean;
}

/** The connected-source vocabulary a channel and a registry row both speak. */
export type InsightsSourceId =
  'shopify' | 'google-ads' | 'meta-ads' | 'linkedin-ads' | 'google-analytics' | 'social';

export const INSIGHTS_CHANNELS: Readonly<Record<InsightsChannelId, InsightsChannelDef>> =
  Object.freeze({
    // 🔴 "All cards", not "All channels" — `50-INSIGHTS-VISUAL-DECISIONS.md`
    // Decision 6, `LIVE-MEASURED` 2026-08-28. This control REPLACES the card
    // set (see the module docblock); `AnalyticsFiltersPopover` 8px to its
    // right already carries "All sources" for the finer-grained scope, and
    // that control's own options — Shopify, Google Ads, Meta Ads, Google
    // Analytics — are the exact same channel-flavoured nouns this list uses.
    // Two triggers reading "All channels" 8px apart, both a dropdown in the
    // same visual register, is the founder's "the way they're filtering on
    // the page is just not okay" — a reader cannot tell, without opening
    // both, that one swaps the card SET and the other narrows the DATA
    // feeding whichever cards are showing. This is the card-set switcher, so
    // its default option says so.
    all: { id: 'all', label: 'Overview', requiresSource: null, hasSpend: true },
    shopify: { id: 'shopify', label: 'Shopify', requiresSource: 'shopify', hasSpend: false },
    'google-ads': {
      id: 'google-ads',
      label: 'Google Ads',
      requiresSource: 'google-ads',
      hasSpend: true,
    },
    'meta-ads': { id: 'meta-ads', label: 'Meta Ads', requiresSource: 'meta-ads', hasSpend: true },
    'linkedin-ads': {
      id: 'linkedin-ads',
      label: 'LinkedIn Ads',
      requiresSource: 'linkedin-ads',
      hasSpend: true,
    },
    ga4: {
      id: 'ga4',
      label: 'Google Analytics',
      requiresSource: 'google-analytics',
      hasSpend: false,
    },
    organic: {
      id: 'organic',
      label: 'Organic social',
      requiresSource: 'social',
      hasSpend: false,
    },
  });

/**
 * Whether a cost-based metric may be REGISTERED for this channel at all.
 *
 * Guarded by `insights-registry.guard.test.ts`, which walks every registry row
 * whose `id` matches a cost metric and fails if it names a channel this returns
 * `false` for. That guard is the thing standing between this page and an
 * "Organic ROAS: —".
 */
export function channelHasSpend(channel: InsightsChannelId): boolean {
  return INSIGHTS_CHANNELS[channel].hasSpend;
}

export function parseInsightsChannel(value: string | null | undefined): InsightsChannelId {
  return value && (INSIGHTS_CHANNEL_IDS as readonly string[]).includes(value)
    ? (value as InsightsChannelId)
    : INSIGHTS_CHANNEL_DEFAULT;
}

/**
 * ── THE TAB REDIRECTS ───────────────────────────────────────────────────────
 * Spec §4.6. A dead deep link is a truthful surface becoming unreachable, which
 * is the same class of defect as deleting it — reports, saved links and two
 * in-app `goToView` call sites all point at these.
 *
 * `competitors` maps to `organic` rather than disappearing: the comparison it
 * made (followers, engagement rate) is an organic comparison, and the
 * CONFIGURATION half moves to Settings → Competitors. Nothing is deleted; see
 * the registry's `competitor-standing` row for where the surviving number went.
 */
export const LEGACY_VIEW_TO_CHANNEL: Readonly<Record<AnalyticsViewId, InsightsChannelId>> =
  Object.freeze({
    overview: 'all',
    content: 'organic',
    audience: 'organic',
    competitors: 'organic',
  });

/**
 * `?platform=` was the OTHER axis and it half-overlapped this one. Every social
 * platform collapses to `organic` — and the platform itself is preserved as a
 * secondary filter within that channel rather than thrown away, which is why
 * this returns a channel and the caller keeps the param.
 */
export const LEGACY_PLATFORM_TO_CHANNEL: Readonly<Record<string, InsightsChannelId>> =
  Object.freeze({
    shopify: 'shopify',
    'google-analytics': 'ga4',
    'google-ads': 'google-ads',
    'meta-ads': 'meta-ads',
    'linkedin-ads': 'linkedin-ads',
    instagram: 'organic',
    facebook: 'organic',
    tiktok: 'organic',
    youtube: 'organic',
    linkedin: 'organic',
    pinterest: 'organic',
  });

const ORGANIC_PLATFORM_IDS = new Set([
  'instagram',
  'facebook',
  'tiktok',
  'youtube',
  'linkedin',
  'pinterest',
]);

/**
 * Keep the card-set channel and the finer report source from contradicting
 * each other. A channel selection never silently leaves an incompatible
 * source active in the URL.
 */
export function reportSourceForChannel(
  channel: InsightsChannelId,
  currentSource: string | null,
): string | null {
  switch (channel) {
    case 'all':
      return null;
    case 'shopify':
      return 'shopify';
    case 'google-ads':
      return 'google-ads';
    case 'meta-ads':
      return 'meta-ads';
    case 'linkedin-ads':
      return 'linkedin-ads';
    case 'ga4':
      return 'google-analytics';
    case 'organic':
      return currentSource && ORGANIC_PLATFORM_IDS.has(currentSource) ? currentSource : null;
  }
}
